import type { Dependency, Severity } from "../types";
import { getAdvisoryDataset, type AdvisoryDataset, type AdvisoryRecord } from "./dataset";
import { rangesCoverVersion } from "./ranges";

/** Which tier produced a match. Both are exact-coordinate; they differ in depth. */
export type AdvisoryTier = "exact" | "ranges";

export interface AdvisoryMatch {
  dependency: Dependency;
  advisory: AdvisoryRecord;
  tier: AdvisoryTier;
  /** True when the coordinate was established by parsing, not by inference. */
  coordinateWasRead: boolean;
}

export interface AdvisoryCheckResult {
  matches: AdvisoryMatch[];
  /** Coordinates that could not be fully resolved, and so were not checked. */
  unresolvable: { source: string; reason: string }[];
  dataset: AdvisoryDataset["manifest"] | null;
}

const UNKNOWN = "unknown";

function isResolved(dependency: Dependency): boolean {
  return Boolean(
    dependency.groupId &&
      dependency.groupId !== UNKNOWN &&
      dependency.artifactId &&
      dependency.artifactId !== UNKNOWN &&
      dependency.version &&
      dependency.version !== UNKNOWN,
  );
}

function coordinateWasRead(dependency: Dependency): boolean {
  const origins = dependency.origins;
  if (!origins) return true;
  return [origins.groupId, origins.artifactId, origins.version].every(
    (origin) => origin === "parsed" || origin === undefined,
  );
}

function uniqueAdvisories(
  dataset: AdvisoryDataset,
  indices: number[],
): AdvisoryRecord[] {
  const seen = new Set<string>();
  const out: AdvisoryRecord[] = [];
  for (const index of indices) {
    const record = dataset.advisories[index];
    if (!record || seen.has(record.id)) continue;
    seen.add(record.id);
    out.push(record);
  }
  return out;
}

/**
 * Look a coordinate up in the shipped dataset.
 *
 * Both tiers answer the same question — is this exact version affected — so
 * the exact tier is consulted first and the broader range tier only fills the
 * artifacts it does not cover. A coordinate that could not be fully resolved
 * is never looked up at all: there is nothing exact to look up, and guessing
 * is the one outcome this check must not produce.
 */
export function matchDependencyAdvisories(
  dependencies: Dependency[],
  dataset: AdvisoryDataset,
): Omit<AdvisoryCheckResult, "dataset"> {
  const matches: AdvisoryMatch[] = [];
  const unresolvable: { source: string; reason: string }[] = [];

  for (const dependency of dependencies) {
    if (!isResolved(dependency)) {
      const missing = (["groupId", "artifactId", "version"] as const).filter(
        (field) => !dependency[field] || dependency[field] === UNKNOWN,
      );
      unresolvable.push({
        source: dependency.source,
        reason: `not checked: ${missing.join(", ")} could not be established`,
      });
      continue;
    }

    const coordinate = `${dependency.groupId}:${dependency.artifactId}`;
    const matchesFor: { record: AdvisoryRecord; tier: AdvisoryTier }[] = [];

    const exact = dataset.exact[coordinate]?.[dependency.version];
    if (exact) {
      for (const record of uniqueAdvisories(dataset, exact)) {
        matchesFor.push({ record, tier: "exact" });
      }
    } else {
      const ranged = dataset.ranges[coordinate];
      if (ranged) {
        for (const [index, ranges] of ranged) {
          if (!rangesCoverVersion(ranges, dependency.version)) continue;
          const record = dataset.advisories[index];
          if (record) matchesFor.push({ record, tier: "ranges" });
        }
      }
    }

    for (const { record, tier } of matchesFor) {
      matches.push({
        dependency,
        advisory: record,
        tier,
        coordinateWasRead: coordinateWasRead(dependency),
      });
    }
  }

  return { matches, unresolvable };
}

export async function checkDependencyAdvisories(
  dependencies: Dependency[],
): Promise<AdvisoryCheckResult> {
  if (!dependencies.length) {
    const { manifest } = await getAdvisoryDataset();
    return { matches: [], unresolvable: [], dataset: manifest };
  }
  const dataset = await getAdvisoryDataset();
  return {
    ...matchDependencyAdvisories(dependencies, dataset),
    dataset: dataset.manifest,
  };
}

/**
 * Map a published CVSS rating onto the four levels findings use.
 *
 * An advisory with no scoreable vector is *not* promoted to a higher level
 * here: it is reported at the lowest level and the finding says upstream
 * published no rating. Inflating an unrated advisory would be inventing data.
 */
export function severityForAdvisory(record: AdvisoryRecord): {
  severity: Severity;
  rated: boolean;
} {
  switch (record.cvssRating) {
    case "CRITICAL":
      return { severity: "critical", rated: true };
    case "HIGH":
      return { severity: "high", rated: true };
    case "MEDIUM":
      return { severity: "medium", rated: true };
    case "LOW":
      return { severity: "low", rated: true };
    default:
      return { severity: "low", rated: false };
  }
}
